
import { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { SortField, SortOrder, ViewMode } from "@/components/candidates/CandidatesViewOptions";

interface CompaniesFilterContextType {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  sortField: SortField;
  setSortField: (field: SortField) => void;
  sortOrder: SortOrder;
  setSortOrder: (order: SortOrder) => void;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  clearAllFilters: () => void;
}

interface CompaniesFilterProviderProps {
  children: ReactNode;
  viewMode?: ViewMode;
  setViewMode?: (mode: ViewMode) => void;
}

const CompaniesFilterContext = createContext<CompaniesFilterContextType | undefined>(undefined);

export function CompaniesFilterProvider({ 
  children, 
  viewMode: externalViewMode, 
  setViewMode: externalSetViewMode 
}: CompaniesFilterProviderProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [internalViewMode, setInternalViewMode] = useState<ViewMode>("card");
  const [currentPage, setCurrentPage] = useState(1);

  // Use either external view mode state or internal
  const viewMode = externalViewMode !== undefined ? externalViewMode : internalViewMode;
  const setViewMode = externalSetViewMode || setInternalViewMode;

  const clearAllFilters = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  return (
    <CompaniesFilterContext.Provider
      value={{
        searchTerm,
        setSearchTerm,
        sortField,
        setSortField,
        sortOrder,
        setSortOrder,
        viewMode,
        setViewMode,
        currentPage,
        setCurrentPage,
        clearAllFilters,
      }}
    >
      {children}
    </CompaniesFilterContext.Provider>
  );
}

export const useCompaniesFilter = () => {
  const context = useContext(CompaniesFilterContext);
  if (context === undefined) {
    throw new Error("useCompaniesFilter must be used within a CompaniesFilterProvider");
  }
  return context;
};
